import React from 'react'
import { Card, Typography, Space, Button, Alert } from 'antd'
import { CodeOutlined, WarningOutlined } from '@ant-design/icons'
import { useLocation } from 'react-router-dom'
import CertificateDemo from '../admin/certificate/CertificateDemo'
import SimpleCertificateTest from '../admin/certificate/SimpleCertificateTest'
import CertificateManagement from '../admin/certificate/CertificateManagement'
import CertificatePositionEditor from '../admin/certificate/CertificatePositionEditor'
import UsageExamples from '../admin/certificate/UsageExamples'
import CertificateBlankoPreview from '../admin/certificate/CertificateBlankoPreview'

const { Title, Text, Paragraph } = Typography

interface DevRouteWrapperProps {
  children: React.ReactNode
  title: string
  description: string
}

const DevRouteWrapper: React.FC<DevRouteWrapperProps> = ({
  children,
  title,
  description,
}) => {
  // Check if we're in development mode
  const isDevelopment = process.env.NODE_ENV === 'development'

  if (!isDevelopment) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <Alert
          message="Development Mode Only"
          description="This page is only available in development mode."
          type="warning"
          showIcon
          icon={<WarningOutlined />}
          style={{ marginBottom: 20 }}
        />
        <Card>
          <Title level={3}>
            <CodeOutlined style={{ marginRight: 8 }} />
            Development Routes
          </Title>
          <Paragraph>
            This page is designed for testing and development purposes only. It
            will not be available in production builds.
          </Paragraph>
          <Button type="primary" href="/">
            Go to Home
          </Button>
        </Card>
      </div>
    )
  }

  return (
    <div style={{ padding: '20px' }}>
      <Alert
        message="Development Mode"
        description="You are viewing a development-only page for testing certificate features."
        type="info"
        showIcon
        icon={<CodeOutlined />}
        style={{ marginBottom: 20 }}
      />
      <Card>
        <Title level={2}>
          <CodeOutlined style={{ marginRight: 8 }} />
          {title}
        </Title>
        <Paragraph>{description}</Paragraph>
      </Card>
      <div style={{ marginTop: 20 }}>{children}</div>
    </div>
  )
}

// Individual route components
export const DevCertificateDemoPage: React.FC = () => (
  <DevRouteWrapper
    title="Certificate Demo"
    description="Interactive demo untuk testing fitur sertifikat dengan preview real-time"
  >
    <CertificateDemo />
  </DevRouteWrapper>
)

export const DevCertificateTestPage: React.FC = () => (
  <DevRouteWrapper
    title="Simple Certificate Test"
    description="Test sederhana untuk mengunduh sertifikat dengan konfigurasi dasar"
  >
    <SimpleCertificateTest />
  </DevRouteWrapper>
)

export const DevCertificateManagementPage: React.FC = () => (
  <DevRouteWrapper
    title="Certificate Management"
    description="Halaman admin untuk mengelola sertifikat (development testing)"
  >
    <CertificateManagement />
  </DevRouteWrapper>
)

export const DevCertificateEditorPage: React.FC = () => (
  <DevRouteWrapper
    title="Certificate Position Editor"
    description="Editor visual untuk mengatur posisi nama pada sertifikat"
  >
    <CertificatePositionEditor
      studentName="Test Student"
      onSave={(config) => {
        console.log('Certificate config saved:', config)
        alert('Konfigurasi tersimpan! Check console untuk detail.')
      }}
    />
  </DevRouteWrapper>
)

export const DevCertificateExamplesPage: React.FC = () => (
  <DevRouteWrapper
    title="Certificate Usage Examples"
    description="Contoh-contoh penggunaan sistem sertifikat"
  >
    <UsageExamples />
  </DevRouteWrapper>
)

export const DevCertificateBlankoPage: React.FC = () => (
  <DevRouteWrapper
    title="Certificate Blanko Preview"
    description="Template sertifikat mirip desain jadi dan siap print ke PDF"
  >
    <CertificateBlankoPreview />
  </DevRouteWrapper>
)

// Development navigation component
export const DevNavigation: React.FC = () => {
  const isDevelopment = process.env.NODE_ENV === 'development'
  const location = useLocation()

  // Only show navigation if we're in development mode AND on a /dev path
  if (!isDevelopment || !location.pathname.startsWith('/dev')) {
    return null
  }

  const devRoutes = [
    {
      path: '/dev/certificate-demo',
      title: 'Certificate Demo',
      description: 'Interactive demo dengan preview real-time',
    },
    {
      path: '/dev/certificate-test',
      title: 'Simple Test',
      description: 'Test sederhana untuk download sertifikat',
    },
    {
      path: '/dev/certificate-management',
      title: 'Certificate Management',
      description: 'Halaman admin untuk mengelola sertifikat',
    },
    {
      path: '/dev/certificate-editor',
      title: 'Position Editor',
      description: 'Editor visual untuk mengatur posisi nama',
    },
    {
      path: '/dev/certificate-examples',
      title: 'Usage Examples',
      description: 'Contoh-contoh penggunaan sistem',
    },
    {
      path: '/dev/certificate-blanko',
      title: 'Blanko Preview',
      description: 'Template sertifikat siap print',
    },
  ]

  return (
    <Card
      title={
        <Space>
          <CodeOutlined />
          Development Routes
        </Space>
      }
      style={{
        position: 'fixed',
        top: 20,
        right: 20,
        width: 300,
        zIndex: 1000,
        maxHeight: '80vh',
        overflow: 'auto',
      }}
    >
      <Space direction="vertical" style={{ width: '100%' }}>
        {devRoutes.map((route) => (
          <Button
            key={route.path}
            type="link"
            href={route.path}
            style={{ textAlign: 'left', height: 'auto', padding: '8px 12px' }}
          >
            <div>
              <Text strong>{route.title}</Text>
              <br />
              <Text type="secondary" style={{ fontSize: '12px' }}>
                {route.description}
              </Text>
            </div>
          </Button>
        ))}
      </Space>
    </Card>
  )
}
